import { createTheme } from '@mui/material/styles';
import { colors, font, layout } from './tokens';

// ─────────────────────────────────────────────────────────────────────────────
// MUI theme built from the design tokens.
//
// tokens.ts stays the source of truth — this file only translates it into the
// shape MUI expects, so that unstyled MUI components inherit the look instead
// of needing an sx prop on every instance.
// ─────────────────────────────────────────────────────────────────────────────

const theme = createTheme({
  palette: {
    mode: 'light',
    primary:   { main: colors.blue, dark: colors.blueDeep, light: colors.blueSoft, contrastText: colors.onDark },
    secondary: { main: colors.accent, dark: colors.accentDeep, light: colors.accentSoft, contrastText: colors.ink },
    error:     { main: colors.alert },
    warning:   { main: colors.warn },
    info:      { main: colors.info },
    success:   { main: colors.ok },
    background: { default: colors.bg, paper: colors.surface },
    text: {
      primary: colors.ink,
      secondary: colors.inkMuted,
      disabled: colors.inkGhost,
    },
    divider: colors.border,
  },

  typography: {
    fontFamily: font.sans,
    // Display / heading scale mirrors type.display and type.heading in tokens.
    h1: { fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.05 },
    h2: { fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1 },
    h3: { fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15 },
    h4: { fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.25 },
    h5: { fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.3 },
    h6: { fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.35 },
    body1: { fontSize: '15px', lineHeight: 1.65 },
    body2: { fontSize: '13px', lineHeight: 1.6 },
    button: { textTransform: 'none', fontWeight: 500, letterSpacing: '0.01em' },
    // Monospace caption used for IDs, timestamps and readings.
    overline: {
      fontFamily: font.mono,
      fontSize: '11px',
      fontWeight: 500,
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
    },
  },

  shape: {
    // MUI wants a number; tokens carry the px string.
    borderRadius: parseInt(layout.radius, 10),
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.bg,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '::selection': {
          backgroundColor: colors.accent,
          color: colors.ink,
        },
      },
    },

    MuiButton: {
      defaultProps: {
        disableRipple: true,
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: layout.radius,
          textTransform: 'none',
        },
        containedPrimary: {
          color: colors.onDark,
          fontWeight: 700,
          '&:hover': { backgroundColor: colors.blueDeep },
        },
        // Styleguide 'green bubble & button' — the brand's CTA shape.
        containedSecondary: {
          borderRadius: layout.radiusPill,
          color: colors.ink,
          fontWeight: 600,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          '&:hover': { backgroundColor: colors.accentPress },
        },
        outlined: {
          borderColor: colors.border,
          color: colors.inkMuted,
          '&:hover': {
            borderColor: colors.ink,
            color: colors.ink,
            backgroundColor: 'transparent',
          },
        },
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: `1px solid ${colors.border}`,
          borderRadius: layout.radiusLg,
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: layout.radius,
          backgroundColor: colors.bg,
          '& fieldset': { borderColor: colors.border },
          '&:hover fieldset': { borderColor: colors.borderStrong },
          '&.Mui-focused fieldset': { borderColor: colors.blue, borderWidth: '1px' },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.dark,
          color: colors.onDark,
          fontFamily: font.mono,
          fontSize: '11px',
          borderRadius: layout.radius,
          padding: '8px 10px',
        },
        arrow: { color: colors.dark },
      },
    },

    MuiCircularProgress: {
      defaultProps: { thickness: 4 },
    },

    MuiDivider: {
      styleOverrides: { root: { borderColor: colors.border } },
    },
  },
});

export default theme;
